import { Button } from '@eventbrite/legacy-eds.button';
import { Icon } from '@eventbrite/legacy-eds.icon';
import { Link as BaseLink } from '@eventbrite/legacy-eds.link';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import styles from './StateMessage.module.scss';

// legacy-eds.link@1.1.0 types its class props as `{ style?, to }` and omits
// `children`, so under @types/react@19 (where class components no longer accept
// children implicitly) JSX children are rejected. The component does accept
// children at runtime (propTypes.children + render `{children}`), so re-type it
// to reflect the real supported props.
const Link = BaseLink as React.ComponentType<{
    to: string;
    style?: string;
    children?: ReactNode;
}>;

interface StateMessageProps {
    title?: MessageDescriptor & { values?: Record<string, string | number> };
    description?: MessageDescriptor & { values?: Record<string, string | number> };
    iconType?: object | ReactElement;
    iconSize?: string;
    primaryLink?: string;
    primaryText?: MessageDescriptor & { values?: Record<string, string | number> };
    buttonStyle?: string;
}

export const StateMessage: React.FunctionComponent<StateMessageProps> = ({
    title,
    description,
    iconType,
    iconSize,
    primaryLink,
    primaryText,
    buttonStyle,
}) => {
    const [isInBrowser, setIsInBrowser] = useState(false);
    const intl = useIntl();

    useEffect(() => setIsInBrowser(true), []);

    const titleText = title ? intl.formatMessage(title, title.values) : '';
    const descriptionText = description ? intl.formatMessage(description, description.values) : '';
    const primaryTextFormatted = primaryText
        ? intl.formatMessage(primaryText, primaryText.values)
        : '';

    return (isInBrowser && (
        <section className={styles.root}>
            <Icon type={iconType} size={iconSize} />
            {titleText && <div className={styles.title}>{titleText}</div>}
            {descriptionText && <p className={styles.description}>{descriptionText}</p>}
            {buttonStyle && primaryLink && primaryTextFormatted && (
                <Link to={primaryLink}>
                    <Button className={styles.primaryButton} style={buttonStyle}>
                        {primaryTextFormatted}
                    </Button>
                </Link>
            )}
            {!buttonStyle && primaryLink && primaryTextFormatted && (
                <Link to={primaryLink}>
                    <p className={styles.link}>{primaryTextFormatted}</p>
                </Link>
            )}
        </section>
    )) as ReactElement;
};
